import { TaskActivity } from "@jugl-web/rest-api/tasks";
import { LiveUpdateSubtaskDto } from "@jugl-web/rest-api/tasks/models/Subtask/SubtaskDto";
import {
  LiveUpdateEvent,
  SUBTASK_ATTACHMENT_MODULE,
  SUBTASK_MODULE,
  TASK_ATTACHMENT_MODULE,
  TASK_CLIENT_FEEDBACK_MODULE,
  TASK_COMMENT_MODULE,
  TASK_MODULE,
} from "../../../live-updates";

export const CREATED_ACTION = "created";
export const ADDED_ACTION = "added";
export const UPDATED_ACTION = "updated";
export const DELETED_ACTION = "deleted";
export const MOVED_ACTION = "moved";
export const ARCHIVED_ACTION = "archived";
export const RESTORED_ACTION = "restored";
export const RENAMED_ACTION = "renamed";
export const REARRANGED_ACTION = "rearranged";
export const ATTACHMENT_ADDED_ACTION = "attachment_added";
export const ATTACHMENT_RENAMED_ACTION = "attachment_renamed";
export const ATTACHMENT_DELETED_ACTION = "attachment_deleted";

// #region Task live updates
export type TaskLiveUpdateEvent<
  TAction extends string = string,
  TData extends object = object,
> = LiveUpdateEvent<TAction, TData, typeof TASK_MODULE>;

export type TaskLiveUpdateCreatedEvent = TaskLiveUpdateEvent<
  typeof CREATED_ACTION,
  {
    id: string;
    entity_id: string;
    time: string;
  }
>;

export type TaskLiveUpdateUpdatedEvent = TaskLiveUpdateEvent<
  typeof UPDATED_ACTION,
  {
    id: string;
    entity_id: string;
    time: string;
  }
>;

export type TaskLiveUpdateDeletedEvent = TaskLiveUpdateEvent<
  typeof DELETED_ACTION,
  {
    id: string;
    entity_id: string;
    time: string;
  }
>;

export type TaskLiveUpdateArchivedEvent = TaskLiveUpdateEvent<
  typeof ARCHIVED_ACTION,
  {
    id: string;
    entity_id: string;
    time: string;
  }
>;

export type TaskLiveUpdateRestoredEvent = TaskLiveUpdateEvent<
  typeof RESTORED_ACTION,
  {
    id: string;
    entity_id: string;
    time: string;
  }
>;
// #endregion

// #region Task attachment live updates
export type TaskAttachmentLiveUpdateEvent<
  TAction extends string = string,
  TData extends object = object,
> = LiveUpdateEvent<TAction, TData, typeof TASK_ATTACHMENT_MODULE>;

export type TaskAttachmentLiveUpdateCreatedEvent =
  TaskAttachmentLiveUpdateEvent<
    typeof ADDED_ACTION,
    {
      entity_id: string;
      task_id: string;
      file_name: string;
    }
  >;

export type TaskAttachmentLiveUpdateRenamedEvent =
  TaskAttachmentLiveUpdateEvent<
    typeof RENAMED_ACTION,
    {
      entity_id: string;
      task_id: string;
      file_name: string;
      old_file_name: string;
    }
  >;

export type TaskAttachmentLiveUpdateDeletedEvent =
  TaskAttachmentLiveUpdateEvent<
    typeof DELETED_ACTION,
    {
      entity_id: string;
      task_id: string;
      file_name: string;
    }
  >;
// #endregion

// #region Task comment live updates
export type TaskCommentLiveUpdateEvent<
  TAction extends string = string,
  TData extends object = object,
> = LiveUpdateEvent<TAction, TData, typeof TASK_COMMENT_MODULE>;

export type TaskCommentLiveUpdateCreatedEvent = TaskCommentLiveUpdateEvent<
  typeof CREATED_ACTION,
  TaskActivity
>;

export type TaskCommentLiveUpdateUpdatedEvent = TaskCommentLiveUpdateEvent<
  typeof UPDATED_ACTION,
  TaskActivity
>;

export type TaskCommentLiveUpdateDeletedEvent = TaskCommentLiveUpdateEvent<
  typeof DELETED_ACTION,
  {
    entity_id: string;
    id: string;
    task_id: string;
  }
>;
// #endregion

// #region Task client feedback live updates
export type TaskClientFeedbackLiveUpdateEvent<
  TAction extends string = string,
  TData extends object = object,
> = LiveUpdateEvent<TAction, TData, typeof TASK_CLIENT_FEEDBACK_MODULE>;

export type TaskClientFeedbackLiveUpdateCreatedEvent =
  TaskClientFeedbackLiveUpdateEvent<typeof CREATED_ACTION, TaskActivity>;

export type TaskClientFeedbackLiveUpdateUpdatedEvent =
  TaskClientFeedbackLiveUpdateEvent<typeof UPDATED_ACTION, TaskActivity>;

export type TaskClientFeedbackLiveUpdateDeletedEvent =
  TaskClientFeedbackLiveUpdateEvent<
    typeof DELETED_ACTION,
    {
      entity_id: string;
      id: string;
      task_id: string;
    }
  >;
// #endregion

// #region Subtask live updates
export type SubtaskLiveUpdateEvent<
  TAction extends string = string,
  TData extends object = object,
> = LiveUpdateEvent<TAction, TData, typeof SUBTASK_MODULE>;

export type SubtaskLiveUpdateCreatedEvent = SubtaskLiveUpdateEvent<
  typeof ADDED_ACTION,
  LiveUpdateSubtaskDto
>;

export type SubtaskLiveUpdateUpdatedEvent = SubtaskLiveUpdateEvent<
  typeof UPDATED_ACTION,
  LiveUpdateSubtaskDto
>;

export type SubtaskLiveUpdateDeletedEvent = SubtaskLiveUpdateEvent<
  typeof DELETED_ACTION,
  {
    entity_id: string;
    task_id: string;
    ids: string[];
  }
>;

export type SubtaskLiveUpdateMovedEvent = SubtaskLiveUpdateEvent<
  typeof MOVED_ACTION,
  {
    entity_id: string;
    src_task_id: string;
    dest_task_id: string;
    ids: string[];
  }
>;

export type SubtaskLiveUpdateRearrangedEvent = SubtaskLiveUpdateEvent<
  typeof REARRANGED_ACTION,
  {
    entity_id: string;
    task_id: string;
    payload: { id: string; order: number }[];
  }
>;
// #endregion

// #region Subtask attachment live updates
export type SubtaskAttachmentLiveUpdateEvent<
  TAction extends string = string,
  TData extends object = object,
> = LiveUpdateEvent<TAction, TData, typeof SUBTASK_ATTACHMENT_MODULE>;

export type SubtaskAttachmentLiveUpdateCreatedEvent =
  SubtaskAttachmentLiveUpdateEvent<
    typeof ADDED_ACTION,
    {
      entity_id: string;
      task_id: string;
      subtask_id: string;
      file_name: string;
    }
  >;

export type SubtaskAttachmentLiveUpdateRenamedEvent =
  SubtaskAttachmentLiveUpdateEvent<
    typeof RENAMED_ACTION,
    {
      entity_id: string;
      task_id: string;
      subtask_id: string;
      file_name: string;
      old_file_name: string;
    }
  >;

export type SubtaskAttachmentLiveUpdateDeletedEvent =
  SubtaskAttachmentLiveUpdateEvent<
    typeof DELETED_ACTION,
    {
      entity_id: string;
      task_id: string;
      subtask_id: string;
      file_name: string;
    }
  >;
// #endregion
